import React, { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import { EGIconButton } from "../../../MainComponents/Button/IconButton/IconButton";
import { AdvancedSearch } from "./AdvancedSearch/AdvanceSearch";
import { BasicSearch } from "./BasicSearch/BasicSearch";
import { useTranslation } from "react-i18next";
import { EGButton } from "../../../MainComponents/Button/EGButton/EGButton";
import { EGPageHeader } from "../../../Title/PageHeader/EGPageHeader";
import { CustomFilterName } from "./CustomFilterList/CustomFilterName";
import { CustomFilterlist } from "./CustomFilterList/CustomFilterlist";
import { IndicatorsAndTags } from "./IndicatorsAndTags/IndicatorsAndTags";
import { useOutletContext } from "react-router";

export const Filters = ({
  open,
  onClose,
  localFilterValues,
  setLocalFilterValues,
  localCatalogSearch,
  setLocalCatalogSearch,
  onSubmitFilters,
  hdlSaveReportFilter,
  filterName,
  setFilterName,
  status,
  tags,
  onApplyFilter,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("report");
  // Initial States
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [isSearchValid, setIsSearchValid] = useState(true);
  const [openNameConfirmation, setOpenNameConfirmation] = useState(false);
  const [resettingCatalogSearch, setResettingCatalogSearch] = useState(false);
  const { savedFilters, reloadSavedFilters } = useOutletContext();
  // At least one value has been selected to sav
  const hasRealFilters = Object.entries(localFilterValues).some(
    ([key, value]) => !["offset", "limit"].includes(key) && Boolean(value)
  );
  // Limit filters to be saved (10)
  const isFilterLimitReached = savedFilters.length >= 10;
  // Validations
  const isFilterSubmissionDisabled =
    (localCatalogSearch.length === 0 && !hasRealFilters) ||
    isFilterLimitReached;

  // Apply filters
  const onApplyFilters = () => {
    onSubmitFilters(localFilterValues, localCatalogSearch);
    onClose();
  };

  // Cancel filter name save
  const handleCancelName = () => {
    setFilterName("");
    setOpenNameConfirmation(false);
  };

  // Confirm filter name and save
  const handleConfirm = async () => {
    await hdlSaveReportFilter(localFilterValues, localCatalogSearch);
    setOpenNameConfirmation(false);
    onClose();
    onSubmitFilters(localFilterValues, localCatalogSearch);
  };

  // Close filters component when press ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (resettingCatalogSearch) {
      setResettingCatalogSearch(false); // clears after reset
    }
  }, [resettingCatalogSearch]);

  // Clean preview selected filters
  const handleClearLocalFilters = () => {
    setLocalCatalogSearch([]);
    setResettingCatalogSearch(true);
    setLocalFilterValues({});
  };

  return (
    <>
      <CustomFilterName
        open={openNameConfirmation}
        onClose={handleCancelName}
        filterName={filterName}
        setFilterName={setFilterName}
        onConfirm={handleConfirm}
      />
      {open && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: theme.zIndex.modal,
            width: "100vw",
            height: "100vh",
            bgcolor: alpha(theme.palette.common.black, 0.5),
            backdropFilter: "blur(1px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 1,
              width: { xs: "95%", md: "80%" },
              height: { xs: "95%", md: "80%" },
              display: "flex",
              flexFlow: "column",
              position: "relative",
            }}
          >
            {/* Close Button  */}
            <Box
              sx={{ position: "absolute", top: 10, right: 10 }}
              onClick={onClose}
            >
              <EGIconButton iconName="cancel_circle" iconColor="primary" />
              <Typography
                variant="body2"
                textAlign="center"
                color="primary"
                mt={-0.7}
                fontSize={8}
                fontWeight={700}
              >
                ESC
              </Typography>
            </Box>
            {/* Title */}
            <Box sx={{ pb: 0.5, mx: 1 }} className="border-bottom">
              <EGPageHeader
                title={t("Filter.filter")}
                subtitle={t("Filter.set_filter")}
                mainIconName="settings3"
              />
            </Box>
            {/* Subtitle & actions */}
            <Stack
              sx={{
                px: 1.5,
                py: 0.5,
                mx: 1,
                my: 0.5,
                alignItems: "center",
                justifyContent: "space-between",
                flexFlow: "row nowrap",
                bgcolor: theme.palette.blueGray50.main,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6">
                {isAdvancedSearch
                  ? t("searchBar.advanced_search")
                  : t("Filter.primary_search_parameters")}
              </Typography>
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "space-around",
                  flexFlow: "row nowrap",
                }}
                spacing={0.5}
              >
                {/* Filter saved list */}
                <Divider sx={{ height: 20, m: 0.33 }} orientation="vertical" />
                <CustomFilterlist
                  savedFilters={savedFilters}
                  reloadSavedFilters={reloadSavedFilters}
                  onApplyFilter={onApplyFilter}
                />
                {/* Clean filter before apply */}
                <Divider sx={{ height: 20, m: 0.33 }} orientation="vertical" />
                <EGIconButton
                  iconName="filter_remove"
                  title={t("Filter.remove_filters")}
                  iconColor="error"
                  iconColorHover="white"
                  colorHover="error"
                  onClick={handleClearLocalFilters}
                />
                {/* Switch basic/advanced search */}
                <Divider sx={{ height: 20, m: 0.33 }} orientation="vertical" />
                <EGIconButton
                  iconName={isAdvancedSearch ? "settings3" : "settings4"}
                  sx={{ transform: !isAdvancedSearch && "rotate(90deg)" }}
                  iconColor="black"
                  title={
                    !isAdvancedSearch
                      ? t("searchBar.advanced_search")
                      : t("Filter.primary_search_parameters")
                  }
                  onClick={() => setIsAdvancedSearch((open) => !open)}
                />
              </Stack>
            </Stack>
            {/* Indicators and Tags */}
            <Box className="border-bottom" mx={1} pb={1.5}>
              <IndicatorsAndTags
                tags={tags}
                filterValues={localFilterValues}
                setFilterValues={setLocalFilterValues}
              />
            </Box>
            {/* Filters content */}
            <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2, px: 2 }}>
              {isAdvancedSearch ? (
                <AdvancedSearch
                  catalogSearch={localCatalogSearch}
                  setCatalogSearch={setLocalCatalogSearch}
                  isResettingCatalog={resettingCatalogSearch}
                />
              ) : (
                <BasicSearch
                  status={status}
                  filterValues={localFilterValues}
                  setFilterValues={setLocalFilterValues}
                  onValidationChange={setIsSearchValid}
                />
              )}
            </Box>
            {/* Filter Footer (Button actions) */}
            <Box
              display="flex"
              alignItems="stretch"
              justifyContent="flex-end"
              py={{ xs: 1, md: 2 }}
              px={{ xs: 1, md: 3.5 }}
              mx={{ xs: 0, md: 2 }}
              sx={{ borderTop: `1px solid ${theme.palette.blueGray200.main}` }}
            >
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: { xs: "center", md: "space-between" },
                  flexFlow: "row wrap",
                  width: "100%",
                }}
                spacing={{ xs: 0.5, sm: 1 }}
              >
                <Box mb={{ xs: 0, sm: 0 }}>
                  <Tooltip
                    TransitionComponent={Zoom}
                    TransitionProps={{ timeout: 150 }}
                    placement="top"
                    title={
                      isFilterLimitReached ? (
                        <span>
                          {t("Filter.limit_reached_delete_one_to_continue")}
                        </span>
                      ) : (
                        ""
                      )
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          textAlign: "justify",
                          maxWidth: 220,
                        },
                      },
                    }}
                    arrow
                    disableHoverListener={!isFilterLimitReached}
                  >
                    <span>
                      <EGButton
                        label={t("Filter.save_and_apply")}
                        color="success"
                        iconName={
                          !isSearchValid || isFilterSubmissionDisabled
                            ? ""
                            : "filter_add"
                        }
                        iconColor="white"
                        iconColorHover="white"
                        labelColor="white"
                        disabled={!isSearchValid || isFilterSubmissionDisabled}
                        onClick={() => {
                          setOpenNameConfirmation(true);
                        }}
                      />
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1, md: 2 } }}>
                  <EGButton
                    label={t("Filter.cancel")}
                    color="secondary"
                    onClick={onClose}
                  />
                  <EGButton
                    label={t("Filter.apply_filter")}
                    color="primary"
                    width={180}
                    onClick={onApplyFilters}
                  />
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};
